import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Alert, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
    Container, Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SaveIcon from '@mui/icons-material/Save';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #eee'
}));

const CompactUploadBtn = styled(Button)(({ theme }) => ({
    border: `1.5px dashed ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: '10px 16px',
    backgroundColor: '#fafafa',
    color: theme.palette.text.secondary,
    height: '56px', 
    width: '100%',
    textTransform: 'none',
    justifyContent: 'flex-start',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f0f7ff',
    }
}));

const TemplateManagement = () => {
    const [templates, setTemplates] = useState([]);
    const [heading, setHeading] = useState('');
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editHeading, setEditHeading] = useState('');
    const [editFile, setEditFile] = useState(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.get(`${API_BASE_URL}/api/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(res.data);
        } catch (err) {
            console.error("Failed to fetch templates", err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!heading || !file) {
            setMessage({ type: 'error', text: 'Please provide a heading and select a file.' });
            return;
        }

        const formData = new FormData();
        formData.append('heading', heading);
        formData.append('file', file);

        setUploading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.post(`${API_BASE_URL}/api/templates`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setMessage({ type: 'success', text: 'Template uploaded successfully!' });
            setHeading('');
            setFile(null);
            document.getElementById('template-file-input').value = '';
            fetchTemplates();
        } catch (err) {
            setMessage({ type: 'error', text: 'Failed to upload template.' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteClick = (template) => {
        setSelectedTemplate(template);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.delete(`${API_BASE_URL}/api/templates/${selectedTemplate.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(templates.filter(t => t.id !== selectedTemplate.id));
            setMessage({ type: 'success', text: 'Template deleted.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Delete failed.' });
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const handleEditClick = (template) => {
        setSelectedTemplate(template);
        setEditHeading(template.heading);
        setEditFile(null);
        setEditDialogOpen(true);
    };

    const handleEditSubmit = async () => {
        const formData = new FormData();
        formData.append('heading', editHeading);
        if (editFile) formData.append('file', editFile);

        setUploading(true);
        try {
            const token = localStorage.getItem('jwtToken');
            const res = await axios.put(`${API_BASE_URL}/api/templates/${selectedTemplate.id}`, formData, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
            });
            setTemplates(templates.map(t => t.id === selectedTemplate.id ? res.data : t));
            setEditDialogOpen(false);
        } catch (err) {
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    const formatFileType = (mimeType) => {
        if (!mimeType) return 'N/A';
        if (mimeType.includes('pdf')) return 'PDF';
        if (mimeType.includes('word') || mimeType.includes('officedocument')) return 'Word';
        if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel';
        if (mimeType.includes('text')) return 'Text';
        return 'File';
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 1, mb: 2 }}>
            
            <StyledPaper elevation={0}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#11224d' }}>
                    Quick Add Template
                </Typography>
                
                {message.text && <Alert severity={message.type} sx={{ mb: 2, py: 0 }}>{message.text}</Alert>}
                
                <Box component="form" onSubmit={handleUpload}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={5}>
                            <TextField
                                label="Heading / Title"
                                value={heading}
                                onChange={(e) => setHeading(e.target.value)}
                                required
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                            <CompactUploadBtn component="label">
                                <CloudUploadIcon sx={{ mr: 1, fontSize: 20 }} />
                                <Typography variant="body2" noWrap sx={{ maxWidth: '180px' }}>
                                    {file ? file.name : "Select Document"}
                                </Typography>
                                <input
                                    id="template-file-input"
                                    type="file"
                                    hidden
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                                />
                            </CompactUploadBtn>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Button 
                                type="submit" 
                                variant="contained" 
                                
                                disabled={uploading}
                                fullWidth
                                startIcon={<SaveIcon />}
                                sx={{ height: '40px', bgcolor: '#11224d', textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                            >
                                {uploading ? '...' : 'Upload'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                        Accepted: PDF, DOC, XLS, TXT
                    </Typography>
                </Box>
            </StyledPaper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#11224d' }}>
                    Existing Templates
                </Typography>
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Heading</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                        ) : templates.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center">No templates found.</TableCell></TableRow>
                        ) : (
                            templates.map((template) => (
                                <TableRow key={template.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <InsertDriveFileIcon fontSize="small" color="action" />
                                            {template.heading}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatFileType(template.file_type)}</TableCell>
                                    <TableCell>{new Date(template.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => handleEditClick(template)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(template)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Template</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Heading"
                        fullWidth
                        value={editHeading}
                        onChange={(e) => setEditHeading(e.target.value)}
                    />
                    <Box sx={{ mt: 2 }}>
                        <Button variant="outlined" component="label" fullWidth>
                            {editFile ? editFile.name : "Replace File (Optional)"}
                            <input type="file" hidden onChange={(e) => setEditFile(e.target.files[0])} />
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" disabled={uploading}>
                        {uploading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete "{selectedTemplate?.heading}"?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TemplateManagement;